import { useCallback, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { TabViewContainer } from '../../../../commonComponents/TabViewContainer';
import { Table } from '../../../../commonComponents/Table/Table';
import { Colors } from '../../../../../themes/colors';
import {
  CandidateStatus,
  JobStatus,
  PermanentJobsService,
  useGetCandidates,
  useToggleShortlistCandidate,
  useUpdateCandidateStatus,
} from '../../../../../domain/PermanentJobs';
import { candidatesColumns } from './candidates-columns';
import { ActionComponent } from './components/ActionComponent';
import { Filters } from './components/Filters';
import { showSuccessMessage } from '../../../../../utils';
import { OnsiteInterviewsPopup } from './components/OnsiteInterviewsPopup';
import { CandidateActionPopup } from './components/CandidateActionPopup';

export const CandidatesScreen = () => {
  const match = useRouteMatch();
  const postingId = match.params.jobId;
  const [selectedProfessionalId, setSelectedProfessionalId] = useState(null);
  const [actionPopup, setActionPopup] = useState({
    show: false,
    candidate: null,
    status: null, // candidate status
    state: 'confirm',
  });

  const {
    data,
    total,
    totalPages,
    filters,
    isLoading,
    handleFilterChange,
    handlePageChange,
    refetch,
  } = useGetCandidates(match.params.jobId, { refetchOnWindowFocus: true });

  const { mutate: toggleShortlist, isPending: isToggleShortlistPending } =
    useToggleShortlistCandidate({
      onSuccess: (variables) => {
        showSuccessMessage({
          message: variables.isShortlisted
            ? 'Candidate removed from shortlist'
            : 'Candidate added to shortlist',
        });
      },
    });

  const { mutate: updateCandidateStatus, isPending: isUpdatingStatus } = useUpdateCandidateStatus({
    onSuccess: () => {
      refetch();
      setActionPopup((prev) => ({ ...prev, state: 'success' }));
    },
    onError: () => {
      setActionPopup({ show: false, candidate: null, status: null, state: 'confirm' });
    },
  });

  const handleRestoreClick = useCallback(
    async (candidate) => {
      // Default to EXPIRED: in production every reject auto-declines any pending
      // interview, so a restored candidate has no actionable interview to confirm.
      let restoreStatus = CandidateStatus.EXPIRED;
      try {
        const response = await PermanentJobsService.getOnsiteInterviewList({
          postingId,
          professionalId: candidate.professionalId,
          page: 1,
          limit: 100,
        });
        const interviews = response?.data ?? [];
        const hasCompletedInterview = interviews.some(
          (interview) => interview.jobStatus === JobStatus.COMPLETE,
        );
        // Defensive fallback — unreachable today because reject auto-declines
        // any active interview, but kept in case that backend behavior changes.
        const hasActiveInterview = interviews.some(
          (interview) =>
            interview.jobStatus === JobStatus.PENDING || interview.jobStatus === JobStatus.COUNTER,
        );
        if (hasCompletedInterview) {
          restoreStatus = CandidateStatus.INTERVIEWED;
        } else if (hasActiveInterview) {
          restoreStatus = CandidateStatus.APPLIED;
        }
      } catch {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch onsite interview list');
      }
      setActionPopup({
        show: true,
        candidate,
        status: restoreStatus,
        state: 'confirm',
      });
    },
    [postingId],
  );

  const handleRejectClick = useCallback((candidate) => {
    setActionPopup({
      show: true,
      candidate,
      status: CandidateStatus.REJECTED,
      state: 'confirm',
    });
  }, []);

  const handleHireClick = useCallback((candidate) => {
    setActionPopup({
      show: true,
      candidate,
      status: CandidateStatus.HIRED,
      state: 'confirm',
    });
  }, []);

  const handleConfirmAction = useCallback(() => {
    if (actionPopup.candidate) {
      updateCandidateStatus({
        candidateIds: [actionPopup.candidate.id],
        status: actionPopup.status,
      });
    }
  }, [actionPopup.candidate, actionPopup.status, updateCandidateStatus]);

  const handleCloseActionPopup = useCallback(() => {
    setActionPopup({ show: false, candidate: null, status: null, state: 'confirm' });
  }, []);

  const getColumn = () =>
    candidatesColumns({
      ActionComponent,
      handleViewOnsiteInterviews: (professionalId) => {
        setSelectedProfessionalId(professionalId);
      },
      handleToggleShortlist: (candidateId, isShortlisted) => {
        toggleShortlist({ candidateId, isShortlisted });
      },
      isToggleShortlistPending,
      onHire: handleHireClick,
      onReject: handleRejectClick,
      onRestore: handleRestoreClick,
    });

  return (
    <>
      <TabViewContainer
        title="Candidates"
        variant="card"
        style={{
          width: '95%',
          maxWidth: 1360,
          paddingBottom: 36,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'row', gap: 20 }}>
          {/* Filters Section */}
          <Filters filters={filters} handleFilterChanged={handleFilterChange} />

          {/* Total Count */}
          <div style={{ alignSelf: 'flex-end' }}>
            <span
              style={{
                fontSize: 20,
                fontWeight: 'bold',
                color: Colors.neutral_600,
              }}
            >
              Total | {total !== undefined ? Intl.NumberFormat().format(total) : ''}
            </span>
          </div>
        </div>

        {/* Table Section */}
        <TabViewContainer style={{ margin: 0, padding: 20, marginTop: 20 }}>
          <Table
            filter={filters}
            columns={getColumn()}
            initialPage={filters.page}
            data={data}
            forceLoading={isLoading}
            noDataComponent="There are no Candidates"
            isLoading={isLoading}
            totalPages={totalPages}
            paginationPerPage={15}
            paginationTotalRows={total}
            onPageChange={handlePageChange}
            showColumnsToHide={false}
            showTotal={false}
            paginationStyle={{ marginBottom: 0 }}
          />
        </TabViewContainer>
      </TabViewContainer>

      {/* Onsite Interviews Popup */}
      {!!selectedProfessionalId && (
        <OnsiteInterviewsPopup
          postingId={postingId}
          professionalId={selectedProfessionalId}
          onClose={() => {
            setSelectedProfessionalId(null);
          }}
        />
      )}

      {/* Candidate Action Popup (Reject/Hire) */}
      {actionPopup.show && (
        <CandidateActionPopup
          status={actionPopup.status}
          candidateName={actionPopup.candidate?.professionalName || ''}
          state={actionPopup.state}
          isLoading={isUpdatingStatus}
          onCancel={handleCloseActionPopup}
          onConfirm={handleConfirmAction}
          onDone={handleCloseActionPopup}
        />
      )}
    </>
  );
};
